/**
 * Mark what the view shows read once it has actually been looked at.
 *
 * The delay is the whole point: a user tabbing past a thread should not clear its badge.
 * The legacy surfaces disagreed on this — the member portal auto-cleared after 5s while
 * the office thread required a manual "Mark as read" click — and the two behaviours were
 * two different mutations against two different tables.
 *
 * Safe to re-fire: `POST /:cid/read` never moves the cursor backwards and records each
 * receipt once, so a re-render, a second tab, or a retry cannot re-light a badge.
 */

import { useEffect, useRef } from "react";

import type { MarkChannelReadInput } from "../_api/channel-endpoints";
import type { ReadOnViewTarget } from "../_libs/read-on-view-target";

export type UseMarkReadOnViewOptions = {
  enabled: boolean;
  /** What to read once the delay passes — `computeReadOnViewTarget` decides. */
  target: ReadOnViewTarget;
  delayMs: number;
  markRead: (read: MarkChannelReadInput) => Promise<unknown>;
};

export function useMarkReadOnView({
  enabled,
  target,
  delayMs,
  markRead,
}: UseMarkReadOnViewOptions): void {
  // The mutation identity changes every render, and the target is a fresh object every
  // render; holding both in refs keeps the timer keyed on what the target says, so a
  // re-render does not restart the countdown.
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;
  const targetRef = useRef(target);
  targetRef.current = target;

  // A primitive standing for the target: the same read keeps the same key.
  const targetKey = target === null ? null : JSON.stringify(target);

  useEffect(() => {
    if (!enabled || targetKey === null) return;

    const timer = setTimeout(() => {
      const read = targetRef.current;
      if (!read) return;
      markReadRef.current(read).catch((error: unknown) => {
        // A failed read is not worth a toast — the badge simply stays lit and the next
        // view retries.
        console.error("Failed to mark channel read:", error);
      });
    }, delayMs);

    return () => clearTimeout(timer);
  }, [enabled, targetKey, delayMs]);
}
